/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  TbLayoutSidebarLeftCollapseFilled,
  TbLayoutSidebarRightCollapseFilled,
} from "react-icons/tb";
import {
  RiLogoutBoxRLine,
  RiArrowDownSLine,
  RiArrowUpSLine,
  RiUser3Line,
  RiPhoneLine,
  RiCloseLine,
  RiMenuLine,
  RiRefreshLine,
  RiMoonClearLine,
  RiSunLine,
} from "react-icons/ri";
import { FaLine, FaCheckCircle } from "react-icons/fa";
import Context from "../../Context";
import Pages from "../../pages/index.js";
import { useToast } from "../../components/ui";
import { getRoleInfo, hasPagePermission } from "../../util/permissionUtils";

export const Navbar = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { profileState, callApi, setProfileState, configs } = Context();
  const [pendingTransferCount, setPendingTransferCount] = useState(0);


  // Sidebar expanded / collapsed state (persisted in localStorage)
  const [showNav, setShowNav] = useState(() => {
    const saved = localStorage.getItem("sidebar_expanded");
    return saved !== null ? saved === "true" : true;
  });

  // Dark mode (Strictly exclusive to user 201989)
  const isUser201989 = String(profileState?.userId) === "201989";
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem("wandee_theme_201989") === "dark";
  });

  const toggleDarkMode = () => {
    if (!isUser201989) return;
    const nextDark = !isDarkMode;
    setIsDarkMode(nextDark);
    if (nextDark) {
      localStorage.setItem("wandee_theme_201989", "dark");
      document.documentElement.classList.add("dark");
    } else {
      localStorage.setItem("wandee_theme_201989", "light");
      document.documentElement.classList.remove("dark");
    }
  };

  useEffect(() => {
    if (isUser201989) {
      setIsDarkMode(localStorage.getItem("wandee_theme_201989") === "dark");
    } else {
      setIsDarkMode(false);
    }
  }, [isUser201989]);

  // Mobile drawer state
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile drawer on route navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  // Track expanded accordion menus by page.path
  const [expandedMenus, setExpandedMenus] = useState({});
  // Hovered item for collapsed flyout
  const [hoveredMenu, setHoveredMenu] = useState(null);

  useEffect(() => {
    localStorage.setItem("sidebar_expanded", showNav ? "true" : "false");
  }, [showNav]);

  // Realtime pending transfer requests count
  useEffect(() => {
    let isMounted = true;
    const fetchCount = async () => {
      try {
        const res = await callApi(
          "orders-getTransferRequests",
          { status: "pending", limit: 50 },
          { notShowLoading: true }
        );
        if (isMounted && res?.status && Array.isArray(res.data)) {
          setPendingTransferCount(res.data.length);
        }
      } catch (e) {
        // silent fail
      }
    };

    fetchCount();
    const interval = setInterval(fetchCount, 5 * 60 * 1000); // Polling ทุก 5 นาทีเพื่อประหยัด Quota

    const handleVisibility = () => {
      if (!document.hidden && isMounted) {
        fetchCount();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      isMounted = false;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);


  const isDev = profileState?.role === "dev";
  const isNavExpanded = showNav || (isDev && mobileOpen);

  // Extract user info
  const fullName = useMemo(() => {
    const fn = [profileState?.firstName, profileState?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    return (
      fn ||
      profileState?.displayName ||
      profileState?.name ||
      profileState?.username ||
      "เจ้าหน้าที่แอดมิน"
    );
  }, [profileState]);

  const phone =
    profileState?.phone ||
    profileState?.tel ||
    profileState?.telephone ||
    profileState?.mobile ||
    "-";

  const lineId =
    profileState?.lineTitle ||
    profileState?.lineName ||
    profileState?.lineId ||
    "-";

  const roleInfo = useMemo(() => {
    return getRoleInfo(profileState?.role, configs);
  }, [profileState?.role, configs]);

  // Filter allowed visible pages (include Home for dev)
  const visiblePages = useMemo(() => {
    return Pages.filter((page) => {
      if (page.path === "/" && isDev) return true;
      if (page.hideSideBar) return false;
      return hasPagePermission(page, profileState, configs);
    });
  }, [profileState, configs, isDev]);

  // Auto-expand parent menu when pathname matches a child
  useEffect(() => {
    visiblePages.forEach((page) => {
      if (page.children && page.children.length > 0) {
        const isChildActive = page.children.some(
          (c) =>
            pathname === c.path ||
            (c.path !== "/" && pathname.startsWith(c.path))
        );
        if (isChildActive) {
          setExpandedMenus((prev) => ({ ...prev, [page.path]: true }));
        }
      }
    });
  }, [pathname, visiblePages]);

  const toggleAccordion = (pagePath) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [pagePath]: !prev[pagePath],
    }));
  };

  const [logoutLoading, setLogoutLoading] = useState(false);

  const handleLogout = async () => {
    if (logoutLoading) return;
    setLogoutLoading(true);

    // 1. Send logout signal to server in background without blocking UI
    try {
      callApi("users-logout", {}).catch((err) => {
        console.warn("Background logout error:", err);
      });
    } catch (err) {
      console.warn("Logout trigger error:", err);
    }

    // 2. Instant local token cleanup & state reset
    localStorage.removeItem("accessToken");
    setProfileState({});
    document.documentElement.classList.remove("dark");

    toast({
      className: "bg-emerald-600 text-white rounded-md",
      title: (
        <div className="flex gap-2 items-center text-sm font-semibold">
          <FaCheckCircle /> ออกจากระบบสำเร็จ
        </div>
      ),
    });

    // 3. Instant navigation to login
    navigate("/login");
  };

  return (
    <>
      {/* Mobile Drawer Backdrop (Dev only) */}
      {isDev && mobileOpen && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-40 md:hidden transition-opacity"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Floating Action Buttons for Mobile (< md) (Dev only: Dark Minimal) */}
      {isDev && (
        <div className="fixed bottom-4 left-4 z-30 md:hidden flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 active:bg-slate-950 text-white px-3 py-2 rounded-md shadow-md border border-slate-700/80 active:scale-95 transition-all text-xs font-semibold cursor-pointer"
            aria-label="เปิดเมนูหลัก"
          >
            <RiMenuLine className="text-base text-slate-200" />
            <span>เมนู</span>
          </button>

          <button
            type="button"
            onClick={() => window.location.reload()}
            className="flex items-center justify-center bg-slate-900 hover:bg-slate-800 active:bg-slate-950 text-slate-200 hover:text-white p-2 rounded-md shadow-md border border-slate-700/80 active:scale-95 transition-all text-xs cursor-pointer"
            title="รีเฟรชหน้าเว็บ"
            aria-label="รีเฟรชหน้าเว็บ"
          >
            <RiRefreshLine className="text-base" />
          </button>
        </div>
      )}

      {/* Floating Sidebar Outer Container */}
      <div
        className={`h-dvh p-2 sm:p-2.5 flex flex-col shrink-0 select-none transition-all duration-300 ${
          isDev
            ? `fixed md:relative inset-y-0 left-0 z-50 md:z-30 ${
                mobileOpen
                  ? "translate-x-0 w-[295px] max-w-[85vw] shadow-2xl"
                  : "-translate-x-full md:translate-x-0"
              } ${showNav ? "md:w-[305px] md:min-w-[305px]" : "md:w-[80px] md:min-w-[80px]"}`
            : `z-30 ${showNav ? "w-[305px] min-w-[305px]" : "w-[80px] min-w-[80px]"}`
        }`}
      >
        {/* Floating Card Shell */}
        <aside className="relative flex flex-col h-full bg-white rounded-md border border-slate-200/90 shadow-md overflow-hidden">
          {/* ================= USER PROFILE HEADER (TOP) ================= */}
          <div className="relative border-b border-slate-200/80 bg-slate-50/90 p-3 flex flex-col justify-between">
            {isNavExpanded ? (
              <div className="flex flex-col gap-2.5">
                <div className="flex items-start justify-between gap-2">
                  {/* User Avatar + Name + Role (Click to go Home for Dev) */}
                  <div
                    onClick={() => {
                      if (isDev) {
                        navigate("/");
                        setMobileOpen(false);
                      }
                    }}
                    className={`flex items-center gap-2.5 min-w-0 flex-1 ${
                      isDev
                        ? "cursor-pointer group hover:bg-slate-200/60 p-1.5 -m-1.5 rounded-md transition-all active:scale-98"
                        : ""
                    }`}
                    title={isDev ? "คลิกเพื่อไปที่หน้าหลัก Dev (Home)" : undefined}
                  >
                    {profileState?.profileImage ? (
                      <img
                        className={`w-10 h-10 rounded-md object-cover border border-slate-200 shadow-2xs shrink-0 ${
                          isDev ? "group-hover:ring-2 group-hover:ring-emerald-400 transition-all" : ""
                        }`}
                        src={profileState.profileImage}
                        alt={fullName}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <div
                        className={`w-10 h-10 rounded-md bg-gradient-to-tr from-emerald-600 to-teal-500 text-white flex items-center justify-center font-bold text-base shadow-sm ring-2 ring-emerald-100 shrink-0 ${
                          isDev ? "group-hover:ring-emerald-400 transition-all" : ""
                        }`}
                      >
                        {fullName?.charAt(0)?.toUpperCase() || <RiUser3Line />}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className={`text-sm font-bold text-slate-900 truncate leading-tight ${
                          isDev ? "group-hover:text-emerald-700 transition-colors" : ""
                        }`}>
                          {fullName}
                        </p>
                        {isDev && (
                          <span className="text-[10px] text-emerald-600 font-extrabold opacity-0 group-hover:opacity-100 transition-opacity">
                            🏠
                          </span>
                        )}
                      </div>
                      <div className="mt-1">
                        <span
                          className={`inline-block px-2 py-0.5 text-[11px] font-semibold rounded-md border shadow-2xs ${roleInfo.color}`}
                          title={roleInfo.desc}
                        >
                          {roleInfo.name}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    {/* Refresh Button for Mobile Drawer (Dev only) */}
                    {isDev && (
                      <button
                        type="button"
                        onClick={() => window.location.reload()}
                        className="md:hidden p-1.5 text-slate-400 hover:text-emerald-700 hover:bg-slate-200/80 rounded-md transition-colors shrink-0 cursor-pointer"
                        title="รีเฟรชหน้าเว็บ"
                      >
                        <RiRefreshLine className="text-xl" />
                      </button>
                    )}

                    {/* Close Button for Mobile Drawer (Dev only) */}
                    {isDev && (
                      <button
                        type="button"
                        onClick={() => setMobileOpen(false)}
                        className="md:hidden p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-200/80 rounded-md transition-colors shrink-0 cursor-pointer"
                        title="ปิดเมนู"
                      >
                        <RiCloseLine className="text-xl" />
                      </button>
                    )}

                    {/* Desktop Collapse Button */}
                    <button
                      type="button"
                      onClick={() => setShowNav(false)}
                      className="hidden md:inline-flex p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-200/80 rounded-md transition-colors shrink-0 cursor-pointer"
                      title="ย่อแถบเมนู"
                    >
                      <TbLayoutSidebarLeftCollapseFilled className="text-xl" />
                    </button>
                  </div>
                </div>

                {/* Contact Details Capsule */}
                <div className="bg-white/80 border border-slate-200/70 rounded-md p-2 space-y-1 text-[12px] text-slate-600 shadow-2xs">
                  <div className="flex items-center gap-1.5 truncate">
                    <RiPhoneLine className="text-slate-400 shrink-0 text-sm" />
                    <span className="text-slate-400 text-[11px]">เบอร์:</span>
                    <span className="font-semibold text-slate-800 truncate font-mono">
                      {phone}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 truncate">
                    <FaLine className="text-[#06C755] shrink-0 text-sm" />
                    <span className="text-slate-400 text-[11px]">LINE:</span>
                    <span className="font-semibold text-slate-800 truncate">
                      {lineId}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              /* Collapsed Mini Profile (Desktop only) */
              <div className="flex flex-col items-center gap-2.5 py-1">
                <button
                  type="button"
                  onClick={() => setShowNav(true)}
                  className="p-1.5 text-slate-400 hover:text-emerald-700 hover:bg-slate-200/80 rounded-md transition-colors cursor-pointer"
                  title="ขยายแถบเมนู"
                >
                  <TbLayoutSidebarRightCollapseFilled className="text-xl text-emerald-600" />
                </button>
                {profileState?.profileImage ? (
                  <img
                    className={`w-9 h-9 rounded-md object-cover border border-slate-200 shadow-sm cursor-pointer ${
                      isDev ? "hover:ring-2 hover:ring-emerald-400 transition-all" : ""
                    }`}
                    src={profileState.profileImage}
                    alt={fullName}
                    title={isDev ? "คลิกเพื่อไปที่หน้าหลัก Dev (Home)" : `${fullName} (${roleInfo.name})\nเบอร์: ${phone}\nLINE: ${lineId}`}
                    onClick={() => {
                      if (isDev) {
                        navigate("/");
                      } else {
                        setShowNav(true);
                      }
                    }}
                  />
                ) : (
                  <div
                    className={`w-9 h-9 rounded-md bg-gradient-to-tr from-emerald-600 to-teal-500 text-white flex items-center justify-center font-bold text-sm shadow-sm ring-2 ring-emerald-100 cursor-pointer ${
                      isDev ? "hover:ring-emerald-400 transition-all" : ""
                    }`}
                    title={isDev ? "คลิกเพื่อไปที่หน้าหลัก Dev (Home)" : `${fullName} (${roleInfo.name})\nเบอร์: ${phone}\nLINE: ${lineId}`}
                    onClick={() => {
                      if (isDev) {
                        navigate("/");
                      } else {
                        setShowNav(true);
                      }
                    }}
                  >
                    {fullName?.charAt(0)?.toUpperCase() || <RiUser3Line />}
                  </div>
                )}
              </div>
            )}
          </div>

        {/* ================= MENU LIST (BODY) ================= */}
        <nav className="flex-1 overflow-y-auto no-scrollbar py-2.5 px-2 space-y-1">
          {visiblePages.map((page) => {
            const hasChildren = page.children && page.children.length > 0;
            const allowedChildren = hasChildren
              ? page.children.filter((c) => {
                if (c.hideSideBar) return false;
                return hasPagePermission(c, profileState, configs);
              })
              : [];

            const isDirectActive = page.component && pathname === page.path;
            const isParentOfActive = allowedChildren.some(
              (c) =>
                pathname === c.path ||
                (c.path !== "/" && pathname.startsWith(c.path))
            );
            const isExpanded = !!expandedMenus[page.path];

            return (
              <div
                key={page.path}
                className="relative"
                onMouseEnter={() => !isNavExpanded && setHoveredMenu(page.path)}
                onMouseLeave={() => !isNavExpanded && setHoveredMenu(null)}
              >
                {/* Main Menu Item */}
                <div
                  onClick={() => {
                    if (page.component) {
                      navigate(page.path);
                      setMobileOpen(false);
                    } else if (allowedChildren.length > 0) {
                      if (!isNavExpanded) {
                        setShowNav(true);
                        setExpandedMenus((prev) => ({
                          ...prev,
                          [page.path]: true,
                        }));
                      } else {
                        toggleAccordion(page.path);
                      }
                    }
                  }}
                  className={`group flex items-center justify-between px-3 py-2 rounded-md text-sm font-semibold transition-all duration-150 cursor-pointer ${isDirectActive
                    ? "bg-emerald-600 text-white shadow-xs font-bold"
                    : isParentOfActive && !isDirectActive
                      ? "bg-emerald-50 text-emerald-800 font-bold border border-emerald-200/80"
                      : "text-slate-700 hover:bg-slate-100 hover:text-slate-950"
                    }`}
                  title={!isNavExpanded ? page.name : undefined}
                >
                  {/* Icon & Label */}
                  <div
                    className={`flex items-center gap-3 min-w-0 ${!isNavExpanded ? "mx-auto justify-center" : ""
                      }`}
                  >
                    <span
                      className={`text-lg shrink-0 transition-colors ${isDirectActive
                        ? "text-white"
                        : isParentOfActive
                          ? "text-emerald-700"
                          : "text-slate-500 group-hover:text-slate-800"
                        }`}
                    >
                      {page.icon}
                    </span>
                    {isNavExpanded && (
                      <span className="truncate text-sm tracking-tight">
                        {page.name}
                      </span>
                    )}
                  </div>

                  {/* Accordion Arrow */}
                  {isNavExpanded && allowedChildren.length > 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleAccordion(page.path);
                      }}
                      className="p-1 text-slate-400 hover:text-slate-700 rounded transition-colors"
                    >
                      {isExpanded ? (
                        <RiArrowUpSLine className="text-base" />
                      ) : (
                        <RiArrowDownSLine className="text-base" />
                      )}
                    </button>
                  )}
                </div>

                {/* Submenu Accordion (Expanded View) */}
                {isNavExpanded && allowedChildren.length > 0 && isExpanded && (
                  <div className="mt-1 ml-4 pl-3 border-l-2 border-slate-200 space-y-0.5">
                    {allowedChildren.map((child, idx) => {
                      const hasExactMatchInSiblings = allowedChildren.some(
                        (s) => s.path === pathname
                      );
                      const isChildActive =
                        pathname === child.path ||
                        (!hasExactMatchInSiblings &&
                          child.path !== "/" &&
                          pathname.startsWith(child.path + "/"));

                      const prevChild = allowedChildren[idx - 1];
                      const showGroupHeader =
                        child.group &&
                        (!prevChild || prevChild.group !== child.group);

                      return (
                        <React.Fragment key={child.path}>
                          {showGroupHeader && (
                            <div className="pt-2.5 pb-1 px-1 flex items-center gap-1.5">
                              <span className="text-[11px] font-bold text-slate-900 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200/90 uppercase tracking-tight">
                                {child.group}
                              </span>
                              <div className="flex-1 h-px bg-slate-200" />
                            </div>
                          )}
                          <div
                            onClick={() => {
                              navigate(child.path);
                              setMobileOpen(false);
                            }}
                            className={`px-2.5 py-1.5 rounded-md text-[13px] font-medium transition-all duration-150 cursor-pointer flex items-center justify-between gap-1.5 ${
                              isChildActive
                                ? "bg-emerald-600 text-white font-bold shadow-2xs"
                                : "text-slate-600 hover:text-slate-950 hover:bg-slate-100"
                            }`}
                            title={child.name}
                          >
                            <span className="truncate">{child.name}</span>
                            {child.path === "/orders/transfer-requests" && pendingTransferCount > 0 && (
                              <span className="px-1.5 py-0.2 rounded-md text-[10px] font-extrabold bg-rose-500 text-white shrink-0 animate-pulse">
                                {pendingTransferCount}
                              </span>
                            )}
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}

                {/* Collapsed Flyout Popover */}
                {!isNavExpanded && hoveredMenu === page.path && (
                  <div className="absolute left-full top-0 ml-3 w-64 max-h-[85vh] overflow-y-auto no-scrollbar bg-white border border-slate-200 rounded-md shadow-xl p-2 z-50 animate-in fade-in-50 duration-150">
                    <div className="px-2 py-1.5 border-b border-slate-100 mb-1 flex items-center gap-2">
                      <span className="text-base text-emerald-600">
                        {page.icon}
                      </span>
                      <span className="font-bold text-xs text-slate-800 truncate">
                        {page.name}
                      </span>
                    </div>
                    {allowedChildren.length > 0 ? (
                      <div className="space-y-0.5">
                        {allowedChildren.map((child, idx) => {
                          const hasExactMatchInSiblings = allowedChildren.some(
                            (s) => s.path === pathname
                          );
                          const isChildActive =
                            pathname === child.path ||
                            (!hasExactMatchInSiblings &&
                              child.path !== "/" &&
                              pathname.startsWith(child.path + "/"));

                          const prevChild = allowedChildren[idx - 1];
                          const showGroupHeader =
                            child.group &&
                            (!prevChild || prevChild.group !== child.group);

                          return (
                            <React.Fragment key={child.path}>
                              {showGroupHeader && (
                                <div className="pt-2 pb-0.5 px-1 flex items-center gap-1">
                                  <span className="text-[10px] font-bold text-slate-900 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200/90 uppercase">
                                    {child.group}
                                  </span>
                                  <div className="flex-1 h-px bg-slate-200" />
                                </div>
                              )}
                              <div
                                key={child.path}
                                onClick={() => {
                                  navigate(child.path);
                                  setHoveredMenu(null);
                                  setMobileOpen(false);
                                }}
                                className={`px-2.5 py-1.5 rounded-md text-xs font-medium cursor-pointer transition-colors truncate ${
                                  isChildActive
                                    ? "bg-emerald-600 text-white font-bold"
                                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                                }`}
                              >
                                {child.name}
                              </div>
                            </React.Fragment>
                          );
                        })}
                      </div>
                    ) : (
                      page.component && (
                        <div
                          onClick={() => {
                            navigate(page.path);
                            setHoveredMenu(null);
                            setMobileOpen(false);
                          }}
                          className="px-2.5 py-1.5 rounded-md text-xs font-medium text-emerald-700 hover:bg-emerald-50 cursor-pointer"
                        >
                          เข้าสู่หน้า {page.name}
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* ================= LOGOUT & DEV THEME BUTTON (BOTTOM) ================= */}
        <div className="p-2.5 border-t border-slate-200/80 bg-slate-50/70 space-y-2">
          {/* Dark Mode Toggle: Exclusive for User 201989 */}
          {isUser201989 && (
            <button
              type="button"
              onClick={toggleDarkMode}
              className={`w-full flex items-center ${
                isNavExpanded
                  ? "justify-between px-3 py-2"
                  : "justify-center p-2"
              } rounded-md text-xs font-semibold cursor-pointer transition-all duration-150 border ${
                isDarkMode
                  ? "bg-[#2d333b] hover:bg-[#373e47] text-amber-300 border-[#444c56] shadow-2xs"
                  : "bg-white hover:bg-slate-100 text-slate-700 border-slate-200 shadow-2xs"
              }`}
              title={isDarkMode ? "เปลี่ยนเป็นธีมสว่าง (Light Mode)" : "เปลี่ยนเป็นธีมมืด (Dark Mode)"}
            >
              <div className="flex items-center gap-2 truncate">
                {isDarkMode ? (
                  <RiMoonClearLine className="text-base text-amber-400 shrink-0" />
                ) : (
                  <RiSunLine className="text-base text-amber-500 shrink-0" />
                )}
                {isNavExpanded && (
                  <span className="truncate">
                    {isDarkMode ? "ธีมมืด (Dark Mode)" : "ธีมสว่าง (Light Mode)"}
                  </span>
                )}
              </div>
              {isNavExpanded && (
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 border border-amber-500/20">
                  201989
                </span>
              )}
            </button>
          )}

          {profileState?.admin ? (
            <button
              type="button"
              disabled={logoutLoading}
              onClick={handleLogout}
              className={`w-full flex items-center ${isNavExpanded
                ? "justify-start px-3 py-2 gap-2.5"
                : "justify-center p-2"
                } rounded-md text-rose-700 bg-rose-50/80 hover:bg-rose-600 hover:text-white border border-rose-200/80 transition-all duration-150 font-semibold text-sm cursor-pointer shadow-2xs disabled:opacity-50`}
              title="ออกจากระบบ"
            >
              <RiLogoutBoxRLine className="text-lg shrink-0" />
              {isNavExpanded && (
                <span className="truncate">
                  {logoutLoading ? "กำลังออกจากระบบ..." : "ออกจากระบบ"}
                </span>
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                navigate("/login");
                setMobileOpen(false);
              }}
              className={`w-full flex items-center ${isNavExpanded
                ? "justify-start px-3 py-2 gap-2.5"
                : "justify-center p-2"
                } rounded-md text-slate-700 bg-slate-100 hover:bg-slate-200 transition-all duration-150 font-semibold text-sm cursor-pointer`}
              title="เข้าสู่ระบบ"
            >
              <RiLogoutBoxRLine className="text-lg shrink-0" />
              {isNavExpanded && <span className="truncate">เข้าสู่ระบบ</span>}
            </button>
          )}
        </div>
      </aside>
    </div>
    </>
  );
};
